<template>
  <div>
    <section>
      <v-container class="fill-height py-8" fluid>
        <v-row align="center" justify="center">
          <v-col cols="12" sm="10" md="7" lg="5" xl="4">
            <div class="v-responsive">
              <div class="v-responsive__content">
                <div class="text-overline secondary--text mb-3">Mira One</div>
                <h2 class="text-h3 mb-6">Mit QR-Code anmelden</h2>
              </div>
            </div>
            <v-card class="pa-4">
              <v-card-text>
                <div v-if="!session" class="text-body-2 text--secondary mb-5">
                  Melde dich schnell und sicher mit einem anderen Gerät an.
                </div>

                <!-- Animated step guide (hidden once session active) -->
                <StepperList
                  v-if="!session"
                  :steps="steps"
                  :current-step="currentStep"
                  :show-next-button="currentStep === 0"
                  @next="goToScannerStep"
                />

                <!-- QR Scanner (shown after step 1 is confirmed) -->
                <div v-if="currentStep >= 1" class="">
                  <div class="qr-scanner-container">
                    <div
                      v-if="
                        shouldShowScannerSurface &&
                        (startingCamera || cameraActive)
                      "
                      class="camera-stage mb-2"
                    >
                      <div
                        v-if="startingCamera && !cameraActive"
                        class="camera-loading"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="48"
                          class="mb-4"
                        />
                        <p class="text-body-1 text--secondary">
                          Warte auf Kamerazugriff …
                        </p>
                        <p class="text-caption text--disabled">
                          Bitte erlaube den Zugriff auf die Kamera, wenn du dazu
                          aufgefordert wirst.
                        </p>
                      </div>

                      <div
                        v-show="cameraActive"
                        ref="videoContainer"
                        class="camera-container video-container default-style"
                        :class="{ 'is-live': cameraActive }"
                      >
                        <video
                          ref="qrVideo"
                          class="qr-video"
                          autoplay
                          muted
                          playsinline
                        ></video>
                      </div>
                    </div>

                    <!-- Initial State - Scanner -->
                    <div v-if="!session">
                      <!-- Processing QR code (after scan, before session created) -->
                      <div v-if="requesting" class="text-center py-6 mb-2">
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="48"
                          class="mb-4"
                        />
                        <p class="text-body-1 text--secondary">
                          QR-Code erkannt – verbinde …
                        </p>
                      </div>

                      <!-- Camera Controls -->
                      <div
                        v-if="
                          !requesting &&
                          cameraActive &&
                          !startingCamera &&
                          !cameraUnavailable &&
                          !cameraPermissionDenied &&
                          (torchSupported || hasMultipleCameras || isIOS)
                        "
                        class="d-flex justify-center align-center mb-3"
                        style="gap: 12px"
                      >
                        <v-btn
                          v-if="torchSupported || isIOS"
                          icon
                          depressed
                          :disabled="!torchSupported"
                          :color="
                            torchEnabled ? 'amber darken-2' : 'grey darken-1'
                          "
                          :title="
                            !torchSupported
                              ? 'Taschenlampe auf diesem Gerät nicht verfügbar'
                              : torchEnabled
                              ? 'Taschenlampe ausschalten'
                              : 'Taschenlampe einschalten'
                          "
                          @click="toggleTorch"
                        >
                          <v-icon>{{
                            torchEnabled
                              ? "mdi-flashlight"
                              : "mdi-flashlight-off"
                          }}</v-icon>
                        </v-btn>

                        <v-btn
                          v-if="hasMultipleCameras || isIOS"
                          icon
                          depressed
                          color="grey darken-1"
                          title="Kamera wechseln"
                          @click="flipCamera"
                        >
                          <v-icon>mdi-camera-flip-outline</v-icon>
                        </v-btn>
                      </div>

                      <p
                        v-if="
                          !requesting &&
                          cameraActive &&
                          !startingCamera &&
                          !cameraUnavailable &&
                          !cameraPermissionDenied
                        "
                        class="text-body-2 text--secondary text-center mb-3"
                      >
                        Richte die Kamera auf den QR-Code
                      </p>

                      <div
                        v-if="
                          !requesting &&
                          !cameraActive &&
                          !cameraUnavailable &&
                          !cameraPermissionDenied &&
                          !startingCamera
                        "
                        class="text-center mb-3"
                      >
                        <v-btn
                          color="primary"
                          large
                          depressed
                          @click="startCamera"
                        >
                          <v-icon left>mdi-camera</v-icon>
                          Kamera starten
                        </v-btn>
                      </div>

                      <!-- Camera Permission Denied -->
                      <v-alert
                        v-if="cameraPermissionDenied"
                        type="error"
                        text
                        class="mb-3"
                      >
                        <div class="subtitle-1 font-weight-medium">
                          Kamerazugriff verweigert
                        </div>
                        <div class="text-body-2 mb-2">
                          Erlaube den Zugriff auf deine Kamera, um den QR-Code
                          zu scannen. Möglicherweise musst du die Berechtigung
                          in den Einstellungen deines Browsers zurücksetzen oder
                          anpassen.
                        </div>
                        <v-btn
                          color="error"
                          depressed
                          small
                          @click="retryCamera"
                        >
                          <v-icon left small>mdi-refresh</v-icon>
                          Erneut versuchen
                        </v-btn>
                      </v-alert>

                      <!-- Camera Unavailable (no camera / unsupported) -->
                      <div
                        v-if="cameraUnavailable && !cameraPermissionDenied"
                        class="text-center mb-3"
                      >
                        <v-icon color="grey" size="48" class="mb-3"
                          >mdi-camera-off</v-icon
                        >
                        <p class="text-body-2 text--secondary">
                          Kamera nicht verfügbar. Bitte nutze die manuelle
                          Eingabe.
                        </p>
                      </div>
                    </div>

                    <!-- Session State - Waiting for Approval -->
                    <div
                      v-else-if="session && session.status === 'requested'"
                      class="text-center py-6"
                    >
                      <v-progress-circular
                        indeterminate
                        color="primary"
                        size="72"
                        class="mb-5"
                        v-if="!approvalOtpSubmitted"
                      ></v-progress-circular>
                      <v-progress-circular
                        v-else
                        indeterminate
                        color="primary"
                        size="72"
                        class="mb-5"
                      ></v-progress-circular>

                      <p class="text-h6 mb-1">
                        {{
                          approvalOtpSubmitted
                            ? "Bestätigungscode wird geprüft …"
                            : statusText
                        }}
                      </p>
                      <p class="text-body-2 text--secondary mb-5">
                        {{
                          approvalOtpSubmitted
                            ? "Bitte warte, bis der Code und die Anmeldung bestätigt wurde."
                            : "Gib den auf dem vertrauenswürdigen Gerät angezeigten Code hier ein."
                        }}
                      </p>

                      <v-card
                        v-if="!approvalOtpSubmitted"
                        outlined
                        class="mb-4 text-left"
                      >
                        <v-card-text class="py-4">
                          <v-text-field
                            v-model="approvalOtpInput"
                            label="Bestätigungscode eingeben"
                            outlined
                            dense
                            maxlength="6"
                            inputmode="numeric"
                          />
                          <v-btn
                            color="primary"
                            block
                            depressed
                            class="mt-3"
                            :loading="verifyingOtp"
                            :disabled="verifyingOtp || !isOtpComplete"
                            @click="verifyApprovalOtp"
                          >
                            Code prüfen
                          </v-btn>
                        </v-card-text>
                      </v-card>
                      <v-btn
                        color="error"
                        depressed
                        small
                        @click="cancelRequest"
                      >
                        <v-icon left small>mdi-close</v-icon>
                        Abbrechen
                      </v-btn>
                    </div>

                    <!-- Login being redeemed / approved -->
                    <div
                      v-else-if="
                        session &&
                        (session.status === 'approved' ||
                          session.status === 'consumed')
                      "
                      class="text-center py-6"
                    >
                      <v-progress-circular
                        indeterminate
                        color="success"
                        size="72"
                        class="mb-5"
                      ></v-progress-circular>

                      <p class="text-h6 mb-1">Anmeldung wird abgeschlossen</p>
                      <p class="text-body-2 text--secondary mb-3">
                        Deine Anmeldung wurde genehmigt. Du wirst gleich
                        weitergeleitet…
                      </p>
                    </div>

                    <!-- Revoked State -->
                    <div
                      v-else-if="session && session.status === 'revoked'"
                      class="text-center py-6"
                    >
                      <v-icon color="error" size="72" class="mb-4"
                        >mdi-close-circle</v-icon
                      >
                      <p class="text-h6 mb-1">Anmeldung abgelehnt</p>
                      <p class="text-body-2 text--secondary mb-5">
                        Die Anmeldesitzung wurde auf dem anderen Gerät
                        abgelehnt.
                      </p>
                      <v-btn color="primary" depressed @click="cancelRequest">
                        <v-icon left>mdi-refresh</v-icon>
                        Erneut versuchen
                      </v-btn>
                    </div>

                    <!-- Expired State -->
                    <div
                      v-else-if="session && session.status === 'expired'"
                      class="text-center py-6"
                    >
                      <v-icon color="error" size="72" class="mb-4"
                        >mdi-timer-off-outline</v-icon
                      >
                      <p class="text-h6 mb-1">Session abgelaufen</p>
                      <p class="text-body-2 text--secondary mb-6">
                        Die Anmeldesitzung ist abgelaufen, da die Anfrage nicht
                        rechtzeitig genehmigt worden ist.
                      </p>
                      <v-btn color="primary" depressed @click="cancelRequest">
                        <v-icon left>mdi-refresh</v-icon>
                        Erneut versuchen
                      </v-btn>
                    </div>

                    <!-- Error Display -->
                    <v-alert
                      v-if="error"
                      type="error"
                      dense
                      text
                      class="mt-4"
                      dismissible
                      @input="error = null"
                    >
                      {{ error }}
                    </v-alert>
                  </div>
                </div>

                <template
                  v-if="
                    !session ||
                    error ||
                    (session &&
                      (session.status === 'revoked' ||
                        session.status === 'expired'))
                  "
                >
                  <v-divider class="my-4"></v-divider>

                  <div class="text-center">
                    <div class="text-caption text--secondary mb-3">
                      Andere Anmeldemethoden
                    </div>
                    <v-btn depressed block :to="{ name: 'auth-login' }">
                      <v-icon left>mdi-login</v-icon>
                      Mit E-Mail anmelden
                    </v-btn>
                  </div>
                </template>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
import { functions, auth, db } from "@/firebase.js";
import QrScanner from "qr-scanner";
import StepperList from "@/components/common/StepperList.vue";
import { USER } from "@/store/modules.js";
import { GET_USER_PROFILE } from "@/store/action-types.js";

export default {
  name: "AuthLoginViaQR",
  components: {
    StepperList,
  },
  data() {
    return {
      currentStep: 0,
      steps: [
        {
          title: "QR-Session starten",
          desc: "Starte das Anmelden mit QR-Code deinem vertrauenswürdigen und bereits angemeldeten Gerät über das Benutzermenü oben rechts.",
        },
        {
          title: "QR-Code scannen",
          desc: "Scanne den angezeigten QR-Code mit der Kamera dieses Geräts.",
        },
        {
          title: "Anfrage genehmigen",
          desc: "Bestätige die Anmeldeanfrage auf deinem vertrauenswürdigen Gerät.",
        },
        {
          title: "Fertig – du bist angemeldet",
          desc: "Du wirst automatisch angemeldet.",
        },
      ],
      // QR Scanner state
      session: null,
      cameraActive: false,
      cameraUnavailable: false,
      cameraPermissionDenied: false,
      cameraPermissionGranted: false,
      hasMultipleCameras: false,
      startingCamera: false,
      facingMode: "environment",
      selectedCameraDeviceId: null,
      availableVideoDevices: [],
      qrScanner: null,
      torchEnabled: false,
      torchSupported: false,
      requesting: false,
      redeeming: false,
      verifyingOtp: false,
      approvalOtpSubmitted: false,
      error: null,
      unsubscribeListener: null,
      deviceInfo: {
        name: this.getDeviceName(),
        type: this.getDeviceType(),
        browser: this.getBrowserInfo(),
        os: this.getOSInfo(),
        language: navigator.language || "unknown",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown",
        screenResolution: `${screen.width}×${screen.height}`,
        userAgent: navigator.userAgent,
      },
      codeVerifier: null,
      codeChallenge: null,
      sessionTimer: null,
      approvalOtpInput: "",
    };
  },
  computed: {
    statusText() {
      switch (this.session?.status) {
        case "pending":
          return "Warte auf Scan …";
        case "requested":
          return "Bestätigungscode eingeben …";
        case "approved":
          return "Genehmigt";
        case "consumed":
          return "Warte auf Genehmigung";
        case "revoked":
          return "Anmeldesitzung wurde zurückgezogen";
        default:
          return "Unbekannt";
      }
    },
    cameraConstraints() {
      return this.selectedCameraDeviceId
        ? { deviceId: { exact: this.selectedCameraDeviceId } }
        : { facingMode: this.facingMode };
    },
    shouldShowScannerSurface() {
      return (
        this.currentStep >= 1 &&
        !this.session &&
        !this.cameraUnavailable &&
        !this.cameraPermissionDenied
      );
    },
    isOtpComplete() {
      return this.approvalOtpInput.replace(/\s+/g, "").length === 6;
    },
    isIOS() {
      const ua = navigator.userAgent || "";
      return (
        /iPad|iPhone|iPod/.test(ua) ||
        (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
      );
    },
  },
  async created() {
    await this.generatePKCEChallenge();
    if (!window.isSecureContext) {
      this.error = "Kamera erfordert eine sichere Verbindung (HTTPS).";
      this.cameraActive = false;
      this.cameraUnavailable = true;
      this.startingCamera = false;
    }
  },
  methods: {
    // Step handling
    goToScannerStep() {
      if (this.currentStep === 1) return;
      this.currentStep = 1;
      this.startCamera();
    },

    async handleLoginSuccess() {
      this.currentStep = 3;
      await this.$store.dispatch(`${USER}${GET_USER_PROFILE}`, {
        uid: auth.currentUser.uid,
        allowRedirect: true,
      });
    },
    handleStepChange(step) {
      if (step === "scanning") this.currentStep = 1;
      else if (step === "waiting") this.currentStep = 2;
      else if (step === "approved") this.currentStep = 3;
    },

    // PKCE (S256 method)
    async generatePKCEChallenge() {
      this.codeVerifier = this.generateRandomString(128);
      this.codeChallenge = await this.generateCodeChallenge(this.codeVerifier);
    },

    generateRandomString(length) {
      if (!window.crypto || !window.crypto.getRandomValues) {
        throw new Error(
          "Cryptographic random number generation is not available. A secure context (HTTPS) is required.",
        );
      }
      const array = new Uint8Array(length);
      window.crypto.getRandomValues(array);
      return btoa(String.fromCharCode.apply(null, array))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "")
        .substring(0, length);
    },

    async generateCodeChallenge(verifier) {
      const encoder = new TextEncoder();
      const data = encoder.encode(verifier);
      const digest = await crypto.subtle.digest("SHA-256", data);
      const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
      return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
    },

    paintQrCodeOutline(detectedCodes, ctx) {
      for (const detectedCode of detectedCodes) {
        const [firstPoint, ...otherPoints] = detectedCode.cornerPoints;
        ctx.strokeStyle = "red";
        ctx.beginPath();
        ctx.moveTo(firstPoint.x, firstPoint.y);
        for (const { x, y } of otherPoints) {
          ctx.lineTo(x, y);
        }
        ctx.lineTo(firstPoint.x, firstPoint.y);
        ctx.closePath();
        ctx.stroke();
      }
    },

    // Device info
    getDeviceName() {
      const ua = navigator.userAgent;
      if (/iPad/.test(ua)) return "iPad";
      if (/iPhone/.test(ua)) return "iPhone";
      if (/Android.*Mobile/.test(ua)) return "Android Smartphone";
      if (/Android/.test(ua)) return "Android Tablet";
      if (/Macintosh/.test(ua)) return "Mac";
      if (/Windows/.test(ua)) return "Windows PC";
      if (/CrOS/.test(ua)) return "Chromebook";
      if (/Linux/.test(ua)) return "Linux PC";
      return "Unbekanntes Gerät";
    },

    getDeviceType() {
      const ua = navigator.userAgent;
      if (/Mobi|Android.*Mobile|iPhone/.test(ua)) return "mobile";
      if (/iPad|Android|Tablet/.test(ua)) return "tablet";
      return "desktop";
    },

    getBrowserInfo() {
      const ua = navigator.userAgent;
      let match;
      if ((match = ua.match(/Firefox\/(\d[\d.]*)/))) {
        return { name: "Firefox", version: match[1] };
      }
      if ((match = ua.match(/Edg\/(\d[\d.]*)/))) {
        return { name: "Microsoft Edge", version: match[1] };
      }
      if ((match = ua.match(/OPR\/(\d[\d.]*)/))) {
        return { name: "Opera", version: match[1] };
      }
      if ((match = ua.match(/Chrome\/(\d[\d.]*)/))) {
        return { name: "Google Chrome", version: match[1] };
      }
      if ((match = ua.match(/Version\/(\d[\d.]*).*Safari/))) {
        return { name: "Safari", version: match[1] };
      }
      return { name: "Unbekannt", version: null };
    },

    getOSInfo() {
      const ua = navigator.userAgent;
      let match;
      if (/Windows NT 10/.test(ua)) return "Windows 10/11";
      if (/Windows/.test(ua)) return "Windows";
      if (/iPad|iPhone/.test(ua)) {
        match = ua.match(/OS ([\d_]+)/);
        return match ? `iOS ${match[1].replace(/_/g, ".")}` : "iOS";
      }
      if ((match = ua.match(/Mac OS X ([\d_.]+)/))) {
        return `macOS ${match[1].replace(/_/g, ".")}`;
      }
      if ((match = ua.match(/Android ([\d.]+)/))) {
        return `Android ${match[1]}`;
      }
      if (/CrOS/.test(ua)) return "Chrome OS";
      if (/Linux/.test(ua)) return "Linux";
      return "Unbekannt";
    },

    // Camera controls
    async startCamera() {
      console.log("Starting camera...");
      this.startingCamera = true;
      this.error = null;
      this.cameraPermissionDenied = false;
      this.cameraUnavailable = false;
      this.cameraPermissionGranted = false;
      this.hasMultipleCameras = false;
      this.availableVideoDevices = [];
      this.torchEnabled = false;
      this.torchSupported = false;

      try {
        if (
          !navigator.mediaDevices ||
          typeof navigator.mediaDevices.getUserMedia !== "function"
        ) {
          this.error =
            "Kamera wird in diesem Browser oder in dieser Umgebung nicht unterstützt.";
          this.cameraUnavailable = true;
          this.cameraActive = false;
          return;
        }

        this.destroyScanner();
        this.cameraPermissionGranted = true;
        await this.$nextTick();

        const videoElement = this.$refs.qrVideo;
        if (!videoElement) {
          throw new Error("Video element is not available.");
        }

        const preferredCamera =
          this.selectedCameraDeviceId || this.facingMode || "environment";

        this.qrScanner = new QrScanner(
          videoElement,
          (result) => this.handleQRDetect(result),
          {
            preferredCamera,
            highlightScanRegion: true,
            highlightCodeOutline: true,
            returnDetailedScanResult: true,
            maxScansPerSecond: 12,
            onDecodeError: () => {},
          },
        );

        await this.qrScanner.start();
        this.cameraActive = true;

        const cameras = await QrScanner.listCameras(true);
        this.availableVideoDevices = cameras;
        this.hasMultipleCameras = cameras.length > 1;

        const defaultCameraId = this.pickDefaultCameraDeviceId(cameras);
        if (defaultCameraId) {
          this.selectedCameraDeviceId = defaultCameraId;
          if (defaultCameraId !== preferredCamera) {
            await this.qrScanner.setCamera(defaultCameraId);
          }
        }

        this.torchSupported = await this.qrScanner.hasFlash();
        if (!this.torchSupported) {
          this.torchEnabled = false;
        }
      } catch (err) {
        console.error("Camera permission error:", err.name, err.message);

        if (err.name === "NotAllowedError") {
          this.cameraPermissionDenied = true;
        } else if (err.name === "NotFoundError") {
          this.error = "Keine Kamera gefunden.";
          this.cameraUnavailable = true;
        } else if (
          err.name === "NotSupportedError" ||
          err.name === "InsecureContextError"
        ) {
          this.error = "Kamera erfordert eine sichere Verbindung (HTTPS).";
          this.cameraUnavailable = true;
        } else if (err.name === "NotReadableError") {
          this.error = "Kamera wird bereits von einer anderen App verwendet.";
        } else {
          this.error = "Kamera konnte nicht gestartet werden";
        }
        this.cameraActive = false;
        this.cameraPermissionGranted = false;
        this.destroyScanner();
      } finally {
        this.startingCamera = false;
      }
    },

    stopCamera() {
      this.destroyScanner();
      this.cameraActive = false;
    },

    async toggleTorch() {
      if (!this.qrScanner || !this.torchSupported) return;
      await this.qrScanner.toggleFlash();
      this.torchEnabled = await this.qrScanner.isFlashOn();
    },

    retryCamera() {
      this.cameraPermissionDenied = false;
      this.cameraPermissionGranted = false;
      this.error = null;
      this.startCamera();
    },

    async flipCamera() {
      const nextDeviceId = this.pickAlternateCameraDeviceId(
        this.availableVideoDevices,
        this.selectedCameraDeviceId,
      );

      if (!this.qrScanner) {
        return;
      }

      const preferredCamera = nextDeviceId || this.swapFacingMode();

      try {
        await this.qrScanner.setCamera(preferredCamera);
        if (nextDeviceId) {
          this.selectedCameraDeviceId = nextDeviceId;
        }
        this.torchSupported = await this.qrScanner.hasFlash();
        if (!this.torchSupported) {
          this.torchEnabled = false;
        }
      } catch (err) {
        console.error("Camera switch error:", err.name, err.message);
        this.error = this.getErrorMessage(err);
      }
    },

    pickDefaultCameraDeviceId(videoDevices) {
      const rearDevice = videoDevices.find((device) =>
        /rear|back|environment|r\u00fcck|dual|ultra-wide|ultra wide/i.test(
          device.label,
        ),
      );

      return rearDevice?.id || videoDevices[0]?.id || null;
    },

    pickAlternateCameraDeviceId(videoDevices, currentDeviceId) {
      if (!videoDevices || videoDevices.length < 2) return null;

      const currentIndex = videoDevices.findIndex(
        (device) => device.id === currentDeviceId,
      );

      if (currentIndex === -1) {
        return this.pickDefaultCameraDeviceId(videoDevices);
      }

      const currentLabel = videoDevices[currentIndex]?.label || "";
      const currentIsFront = /front|user|vorder/i.test(currentLabel);

      const oppositeDevice = videoDevices.find((device) => {
        const isFront = /front|user|vorder/i.test(device.label || "");
        return currentIsFront ? !isFront : isFront;
      });

      return (
        oppositeDevice?.id ||
        videoDevices[(currentIndex + 1) % videoDevices.length].id
      );
    },

    swapFacingMode() {
      this.facingMode =
        this.facingMode === "environment" ? "user" : "environment";
      return this.facingMode;
    },

    destroyScanner() {
      if (this.qrScanner) {
        this.qrScanner.destroy();
        this.qrScanner = null;
      }
    },

    onCameraError(err) {
      console.error("Camera error:", err.name, err.message);

      if (
        (err.name === "OverconstrainedError" ||
          err.name === "ConstraintNotSatisfiedError") &&
        !this.cameraFacingFallback
      ) {
        this.cameraFacingFallback = true;
        this.cameraActive = false;
        this.$nextTick(() => {
          this.cameraActive = true;
        });
        return;
      }

      // NotAllowedError is handled in startCamera() via getUserMedia.
      // Other errors may still be emitted by the component.
      if (err.name === "NotFoundError") {
        this.error = "Keine Kamera gefunden.";
        this.cameraUnavailable = true;
      } else if (
        err.name === "NotSupportedError" ||
        err.name === "InsecureContextError"
      ) {
        this.error = "Kamera erfordert eine sichere Verbindung (HTTPS).";
        this.cameraUnavailable = true;
      } else if (err.name === "NotReadableError") {
        this.error = "Kamera wird bereits von einer anderen App verwendet.";
      } else if (err.name === "StreamApiNotSupportedError") {
        this.error =
          "Kamera-Streaming wird in diesem Browser nicht unterstützt.";
        this.cameraUnavailable = true;
      } else if (err.name === "NotAllowedError") {
        // Fallback in case component still emits it
        this.cameraPermissionDenied = true;
        this.cameraPermissionGranted = false;
      } else {
        this.error = `Kamera konnte nicht gestartet werden: ${err.message}`;
      }
      this.cameraActive = false;
      this.startingCamera = false;
    },

    // QR detection & login flow
    async handleQRDetect(result) {
      const qrData = result?.data || result?.content || result?.rawValue;
      if (!qrData) return;
      try {
        const payload = JSON.parse(qrData);
        if (payload.sid && payload.transferId) {
          this.stopCamera();
          await this.requestLoginWithSid(payload.sid, payload.transferId);
        }
      } catch (error) {
        console.error("Ungültiges QR-Code-Format:", error);
      }
    },

    async requestLoginWithSid(sid, transferId) {
      if (!sid || !transferId) return;

      this.requesting = true;
      this.error = null;

      try {
        const requestLoginFn = functions.httpsCallable("qrLogin-requestLogin");
        await requestLoginFn({
          sid,
          transferId,
          deviceInfo: this.deviceInfo,
          codeChallenge: this.codeChallenge,
        });

        this.session = {
          sid,
          transferId,
          status: "requested",
          approvalOtp: null,
          approvalOtpVerified: false,
        };
        this.approvalOtpSubmitted = false;
        this.handleStepChange("waiting");
        this.startListening(transferId);
        this.startSessionTimer();
      } catch (error) {
        console.error("Fehler beim Senden der Anmeldeanfrage:", error);
        this.error = this.getErrorMessage(error);
      } finally {
        this.requesting = false;
      }
    },

    async redeemLogin() {
      if (!this.session) return;

      this.redeeming = true;
      this.error = null;

      try {
        await auth.setPersistence("session");

        const redeemLoginFn = functions.httpsCallable("qrLogin-redeemLogin");
        const result = await redeemLoginFn({
          sid: this.session.sid,
          codeVerifier: this.codeVerifier,
        });

        await auth.signInWithCustomToken(result.data.customToken);

        this.session.status = "consumed";
        this.stopListening();
        this.handleStepChange("approved");
        this.handleLoginSuccess();
      } catch (error) {
        console.error("Fehler beim Einlösen der Anmeldung:", error);
        this.error = this.getErrorMessage(error);
      } finally {
        this.redeeming = false;
      }
    },

    async cancelRequest() {
      this.session = null;
      this.currentStep = 0;
      this.stopListening();
      this.stopSessionTimer();
      await this.generatePKCEChallenge();
      this.startCamera();
      this.approvalOtpInput = "";
      this.approvalOtpSubmitted = false;
    },

    async verifyApprovalOtp() {
      if (!this.session || !this.isOtpComplete) return;

      this.verifyingOtp = true;
      this.approvalOtpSubmitted = true;
      this.error = null;

      try {
        const verifyOtpFn = functions.httpsCallable(
          "qrLogin-verifyApprovalOtp",
        );
        await verifyOtpFn({
          sid: this.session.sid,
          approvalOtp: this.approvalOtpInput.replace(/\s+/g, ""),
        });

        this.session = {
          ...this.session,
          approvalOtpVerified: true,
        };
      } catch (error) {
        console.error("Fehler bei der OTP-Prüfung:", error);
        this.error = this.getErrorMessage(error);
      } finally {
        this.verifyingOtp = false;
      }
    },

    startListening(transferId) {
      this.stopListening();

      this.unsubscribeListener = db
        .collection("ephemeralSessionTransfers")
        .doc(transferId)
        .onSnapshot(
          (doc) => {
            if (!doc.exists) {
              this.error = "Session nicht gefunden.";
              this.session = null;
              this.stopListening();
              return;
            }

            const data = doc.data();
            const previousStatus = this.session?.status;

            // Update local session status from transfer doc (contains only status)
            this.session = {
              ...this.session,
              status: data.status,
              approvalOtp:
                typeof data.approvalOtp === "string"
                  ? data.approvalOtp
                  : this.session.approvalOtp,
              approvalOtpVerified:
                typeof data.approvalOtpVerified === "boolean"
                  ? data.approvalOtpVerified
                  : this.session.approvalOtpVerified,
            };

            // Handle status transitions
            if (data.status === "approved" && previousStatus !== "approved") {
              this.stopSessionTimer();
              this.handleStepChange("approved");
              this.redeemLogin();
            } else if (
              data.status === "revoked" &&
              previousStatus !== "revoked"
            ) {
              this.session = { ...this.session, status: "revoked" };
              this.stopListening();
            }
          },
          (error) => {
            console.error("Firestore listener error:", error);
            this.error =
              "Verbindung zur Session verloren. Bitte erneut versuchen.";
            this.session = null;
            this.stopListening();
          },
        );
    },

    stopListening() {
      if (this.unsubscribeListener) {
        this.unsubscribeListener();
        this.unsubscribeListener = null;
      }
    },

    startSessionTimer() {
      this.stopSessionTimer();
      this.sessionTimer = setTimeout(() => {
        if (
          this.session &&
          this.session.status !== "approved" &&
          this.session.status !== "consumed"
        ) {
          this.session = { ...this.session, status: "expired" };
          this.stopListening();
        }
      }, 120 * 1000);
    },

    stopSessionTimer() {
      if (this.sessionTimer) {
        clearTimeout(this.sessionTimer);
        this.sessionTimer = null;
      }
    },

    getErrorMessage(error) {
      if (error.code === "functions/not-found") {
        return "Session nicht gefunden.";
      } else if (error.code === "functions/deadline-exceeded") {
        return "Session ist abgelaufen.";
      } else if (error.code === "functions/permission-denied") {
        return "Berechtigung verweigert.";
      } else if (error.code === "functions/failed-precondition") {
        return "Session ist nicht in einem gültigen Zustand.";
      }
      return "Ein Fehler ist aufgetreten. Bitte versuche es erneut.";
    },

    formatOtp(otp) {
      if (!otp) return "";
      return String(otp).replace(/(\d{3})(\d{3})/, "$1 $2");
    },
  },

  beforeDestroy() {
    this.stopCamera();
    this.stopListening();
    this.stopSessionTimer();
  },
};
</script>

<style scoped>
.qr-scanner-container {
  width: 100%;
}

.camera-stage {
  width: 100%;
  min-height: min(300px, 40vw);
}

.camera-loading {
  width: 100%;
  min-height: min(300px, 40vw);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.04);
}

.camera-container {
  width: 100%;
  height: min(300px, 40vw);
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: transparent;
}

.camera-container.is-live {
  background: #000;
}

.video-container.default-style {
  line-height: 0;
}

.video-container.example-style-1 :deep(.scan-region-highlight-svg),
.video-container.example-style-1 :deep(.code-outline-highlight) {
  stroke: #64a2f3 !important;
}

.video-container.example-style-2 {
  position: relative;
  width: max-content;
  height: max-content;
  overflow: hidden;
}

.video-container.example-style-2 :deep(.scan-region-highlight) {
  border-radius: 30px;
  outline: rgba(0, 0, 0, 0.25) solid 50vmax;
}

.video-container.example-style-2 :deep(.scan-region-highlight-svg) {
  display: none;
}

.video-container.example-style-2 :deep(.code-outline-highlight) {
  stroke: rgba(255, 255, 255, 0.5) !important;
  stroke-width: 15 !important;
  stroke-dasharray: none !important;
}

.qr-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
